import type { FC, ReactNode } from 'react';

export type DefaultSectionProps = {
  /**
   * Display label shown in the accordion header.
   */
  label: string;
  /**
   * Optional accordion description.
   */
  description?: string;
  /**
   * Field components belonging to this section. Each direct child must carry a
   * unique React `key`, same contract as flat `CustomisableFieldWrapper` children.
   */
  children: ReactNode | ReactNode[];
};

/**
 * Marker component for declaring a default form section inline in JSX.
 *
 * `<DefaultSection>` does not render its own DOM. `CustomisableFieldWrapper`
 * detects it by component identity, flattens its children into the field list,
 * and synthesises a section config that feeds `useSectionManager`.
 *
 * The section's stable identifier is the React `key` on the `<DefaultSection>`
 * element. Use a `default_<entity>_<name>` prefix to avoid collision with
 * customer-defined `form_section_configuration.SectionId` UUIDs.
 *
 * @example
 *   <CustomisableFieldWrapper>
 *     <DefaultSection key="default_risk_details" label="Details">
 *       <Field key="Title" ... />
 *       <Field key="Description" ... />
 *     </DefaultSection>
 *     <DefaultSection key="default_risk_schedule" label="Test schedule">
 *       <Field key="TestFrequency" ... />
 *     </DefaultSection>
 *   </CustomisableFieldWrapper>
 *
 * Customer-defined sections (from `form_section_configuration`) take precedence
 * over these defaults; see `useSectionManager` for the merge rules.
 */
const DefaultSection: FC<DefaultSectionProps> = () => null;

export default DefaultSection;
